import { from } from 'rxjs';
import { filter } from 'rxjs/operators';
import { setAttendeeName, clearKnock } from '@shoflo/studio-redux/actions';
import {
  BROADCAST_ACCEPT_KNOCK,
  BROADCAST_UPDATE_KNOCK
} from '@shoflo/studio-redux/actions/types';
import { getBroadcastRouteMatch } from '@shoflo/studio-redux/utils';
import {
  getApiClient,
  isValidKnock,
  handleFailedCreateAttendee,
  sendMeetingToAttendee,
  getKnockPayload,
  createAttendee,
  getGenericKnockPayload
} from '../utils';
import { createSource } from 'actions/source';
import { createSourceFactory } from 'utils/actionUtils';
import { SOURCE_ROLE_SPEAKER } from '@shoflo/polymer';
import { shouldHaveFeature } from '@studio/feature.flags/featureFlags';
import { VENDOR_AGORA } from '@studio/constants/Vendor';

/**
 * Emits an event to the accepted Presenter socket
 *
 * @param {*} socket
 */
export function acceptedKnockMiddleware(socket) {
  return store => next => async action => {
    const targetdActions = [
      `${BROADCAST_ACCEPT_KNOCK}`,
      `${BROADCAST_UPDATE_KNOCK}_SUCCESS`
    ];
    from([action])
      .pipe(filter($action => targetdActions.includes($action.type)))
      .subscribe(async $action => {
        if (!isValidKnock($action)) return;
        const apiClient = getApiClient();

        const { dispatch, getState } = store;

        let {
          meeting,
          attendeeRoster,
          program,
          team,
          attendeeCameraStatus,
          broadcastSettings,
          banners,
          streamEndpoints
        } = getState();

        const match = getBroadcastRouteMatch(getState);

        const { teamId, eventId, broadcastId } = match.params;
        const useAgora =
          shouldHaveFeature('studio.meetingVendor') === VENDOR_AGORA;

        if (useAgora) {
          const payload = getGenericKnockPayload({
            getState,
            action: $action,
            banners,
            streamEndpoints,
            attendeeRoster,
            program,
            broadcastSettings,
            team
          });

          sendMeetingToAttendee(socket, payload);
          dispatch(clearKnock($action.payload));
          return;
        }
        // Check to see if the meeting is in session
        if (!meeting.meetingSession) return;

        // Clear the knock from state
        dispatch(clearKnock($action.payload));

        let createdAttendee;

        // Create the attendee
        try {
          createdAttendee = await createAttendee(
            apiClient,
            $action,
            teamId,
            eventId,
            meeting
          );
        } catch (e) {
          handleFailedCreateAttendee(e, socket, $action, store);
        }

        if (!createdAttendee) return;

        const attendeeId = createdAttendee.attendeeResponse.Attendee.AttendeeId;

        // The 'presenterSessionId' is utilized when a presenter joins a Chime meeting SLI.
        // It follows the format 'presenterSessionId: Presenter-{presenterId}-{sessionId}'.
        const presenterSessionId =
          createdAttendee.attendeeResponse.Attendee.ExternalUserId;

        dispatch(
          setAttendeeName(
            attendeeId,
            $action.payload.name,
            '',
            SOURCE_ROLE_SPEAKER
          )
        );

        const knockPayload = getKnockPayload({
          getState,
          action: $action,
          createdAttendee,
          attendeeCameraStatus,
          banners,
          streamEndpoints,
          meeting,
          attendeeRoster,
          program,
          broadcastSettings,
          team
        });

        sendMeetingToAttendee(socket, knockPayload);

        const newSource = createSourceFactory({
          broadcastId,
          eventId,
          teamId,
          attendeeId,
          title: $action.payload.name,
          description: $action.payload.description,
          isInitializing: true, // Initialize source at creation to avoid making two dispatch calls,
          presenterPhoto: $action.payload?.presenterPhoto,
          userId: $action?.payload?.userId,
          role: SOURCE_ROLE_SPEAKER,
          presenterSessionId,
          cameraUnavailable: $action.payload.cameraUnavailable
        });

        store.dispatch(createSource(newSource, true));
      });
    return next(action);
  };
}

export default acceptedKnockMiddleware;
